import React, { useEffect, useMemo } from 'react';
import {
  TableContainer,
  Paper,
  TableHead,
  Checkbox,
  useTheme,
  Table,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Box,
  Stack,
  Input,
  InputAdornment
} from '@mui/material';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import {
  IEcommerceProductHierarchy,
  IEcommerceProductHierarchyItem,
  IEcommerceSubBrandHierarchy
} from 'src/models/ecommerce';
import Empty from 'src/components/Empty';
import ProductPostCard from 'src/components/ProductPostCard';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import { useUnlimitedScroll } from 'src/hooks/useUnlimitedScroll';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Search } from '@mui/icons-material';
interface TableProps {
  data: IEcommerceProductHierarchy[];
  loading: boolean;
  selectedProducts: IEcommerceProductHierarchyItem[];
  onSelectedProductsChange?: (
    products: IEcommerceProductHierarchyItem[]
  ) => void;
  setSelectedSubBrand: React.Dispatch<
    React.SetStateAction<IEcommerceSubBrandHierarchy[]>
  >;
  setSelectedBrand: React.Dispatch<
    React.SetStateAction<IEcommerceProductHierarchy[]>
  >;
}

const FilterProductList: React.FC<TableProps> = ({
  data,
  loading,
  selectedProducts,
  onSelectedProductsChange,
  setSelectedSubBrand,
  setSelectedBrand
}) => {
  const theme = useTheme();

  // Helper function to find product by id and originalId
  const isProductSelected = (product: IEcommerceProductHierarchyItem) => {
    return selectedProducts.some(
      (p) => p.id === product.id && p.originalId === product.originalId
    );
  };

  // Helper function to get all products from a subbrand
  const getSubbrandProducts = (subbrand: IEcommerceSubBrandHierarchy) => {
    return subbrand.products || [];
  };

  // Helper function to get all products from a brand
  const getBrandProducts = (brand: IEcommerceProductHierarchy) => {
    const products: IEcommerceProductHierarchyItem[] = [];
    brand.subBrands?.forEach((subbrand) => {
      products.push(...getSubbrandProducts(subbrand));
    });
    return products;
  };

  // Check if all products in a subbrand are selected
  const isSubbrandFullySelected = (subbrand: IEcommerceSubBrandHierarchy) => {
    const products = getSubbrandProducts(subbrand);
    if (products.length === 0) return false;
    return products.every((p) => isProductSelected(p));
  };

  // Check if some (but not all) products in a subbrand are selected
  const isSubbrandIndeterminate = (subbrand: IEcommerceSubBrandHierarchy) => {
    const products = getSubbrandProducts(subbrand);
    if (products.length === 0) return false;
    const selectedCount = products.filter((p) => isProductSelected(p)).length;
    return selectedCount > 0 && selectedCount < products.length;
  };

  // Check if all products in a brand are selected
  const isBrandFullySelected = (brand: IEcommerceProductHierarchy) => {
    const products = getBrandProducts(brand);
    if (products.length === 0) return false;
    return products.every((p) => isProductSelected(p));
  };

  // Check if some (but not all) products in a brand are selected
  const isBrandIndeterminate = (brand: IEcommerceProductHierarchy) => {
    const products = getBrandProducts(brand);
    if (products.length === 0) return false;
    const selectedCount = products.filter((p) => isProductSelected(p)).length;
    return selectedCount > 0 && selectedCount < products.length;
  };

  // Handle product checkbox change
  const handleProductCheckChange = (
    product: IEcommerceProductHierarchyItem,
    checked: boolean
  ) => {
    let updatedProducts: IEcommerceProductHierarchyItem[];

    if (checked) {
      // Add product if not already selected
      if (!isProductSelected(product)) {
        updatedProducts = [...selectedProducts, product];
      } else {
        updatedProducts = selectedProducts;
      }
    } else {
      // Remove product
      updatedProducts = selectedProducts.filter(
        (p) => !(p.id === product.id && p.originalId === product.originalId)
      );
    }

    onSelectedProductsChange?.(updatedProducts);
  };

  // Get all products from all brands
  const getAllProducts = () => {
    const allProducts: IEcommerceProductHierarchyItem[] = [];
    data.forEach((brand) => {
      allProducts.push(...getBrandProducts(brand));
    });
    return allProducts;
  };

  // Check if all products are selected
  const isAllProductsSelected = () => {
    const allProducts = getAllProducts();
    if (allProducts.length === 0) return false;
    return allProducts.every((p) => isProductSelected(p));
  };

  // Check if some (but not all) products are selected
  const isSelectAllIndeterminate = () => {
    const allProducts = getAllProducts();
    if (allProducts.length === 0) return false;
    const selectedCount = allProducts.filter((p) =>
      isProductSelected(p)
    ).length;
    return selectedCount > 0 && selectedCount < allProducts.length;
  };

  // Handle select all checkbox change
  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      // Select all products
      const allProducts = getAllProducts();
      onSelectedProductsChange?.(allProducts);
    } else {
      // Clear all selections
      onSelectedProductsChange?.([]);
    }
  };

  const addName = useMemo((): Array<IEcommerceProductHierarchyItem> => {
    return data
      .flatMap((brand) =>
        brand.subBrands.flatMap((subBrand) => subBrand.products)
      )
      .map((product) => ({
        ...product,
        name: product.name
      }));
  }, [data]);

  const { filteredList: unsortedFilteredList, handleFilter } =
    useFiltersByName<IEcommerceProductHierarchyItem>(addName);

  const filteredList = useMemo(() => {
    if (!unsortedFilteredList.length) return [];
    if (!selectedProducts.length)
      return unsortedFilteredList.sort((a, b) => a.name.localeCompare(b.name));

    const activeIdsSet = new Set(selectedProducts.map((item) => item.id));
    return unsortedFilteredList.sort((a, b) => {
      const aIsActive = activeIdsSet.has(a.id);
      const bIsActive = activeIdsSet.has(b.id);
      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;
      return 0;
    });
  }, [unsortedFilteredList, selectedProducts]);

  const { fetchMoreData, infiniteScroll } =
    useUnlimitedScroll<IEcommerceProductHierarchyItem>(filteredList);

  useEffect(() => {
    const selectedSubBrand = data
      .flatMap((brand) => brand.subBrands ?? [])
      .filter(
        (subbrand) =>
          isSubbrandFullySelected(subbrand) || isSubbrandIndeterminate(subbrand)
      );

    const selectedBrand = data.filter(
      (brand) => isBrandFullySelected(brand) || isBrandIndeterminate(brand)
    );

    setSelectedBrand(selectedBrand);
    setSelectedSubBrand(selectedSubBrand);
  }, [selectedProducts, data]);

  return (
    <>
      <Input
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        fullWidth
        placeholder="Search Product"
        onChange={(event) => handleFilter(event.target.value)}
      />
      <TableContainer
        component={Paper}
        sx={{
          mt: theme.spacing(2)
        }}
      >
        <Table stickyHeader>
          <TableHead
            sx={{
              background: theme.palette.primary.main
            }}
          >
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: '3%' }}>
                <Checkbox
                  checked={isAllProductsSelected()}
                  indeterminate={isSelectAllIndeterminate()}
                  size="small"
                  onChange={handleSelectAllChange}
                  icon={
                    <FilterNoneIcon
                      sx={{
                        color: theme.palette.common.white,
                        width: theme.spacing(2.5),
                        height: theme.spacing(2.5)
                      }}
                    />
                  }
                  checkedIcon={
                    <LibraryAddCheckIcon
                      sx={{
                        color: theme.palette.common.white,
                        width: theme.spacing(2.5),
                        height: theme.spacing(2.5)
                      }}
                    />
                  }
                  indeterminateIcon={
                    <IndeterminateCheckBoxIcon
                      sx={{
                        color: theme.palette.common.white,
                        width: theme.spacing(2.5),
                        height: theme.spacing(2.5)
                      }}
                    />
                  }
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow hover={false}>
                <TableCell align="center" colSpan={4}>
                  <Box
                    sx={{
                      height: 300,
                      background: theme.palette.grey[200],
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: theme.palette.grey[200],
                      opacity: '0.3'
                    }}
                  >
                    <CircularProgress disableShrink color="primary" size={64} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : data.length < 0 ? (
              <TableRow hover={false}>
                <TableCell align="center" colSpan={4}>
                  <Box
                    sx={{
                      height: 300,
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <Empty message="There's no data yet" />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              <InfiniteScroll
                dataLength={infiniteScroll.list.length}
                next={fetchMoreData}
                hasMore={infiniteScroll.hasMore}
                loader={<div />}
                height={300}
                endMessage={<div />}
              >
                {infiniteScroll.list.map((row) => (
                  <ProductCheckRow
                    key={row.id}
                    product={row}
                    isSelected={isProductSelected(row)}
                    onCheckChange={handleProductCheckChange}
                  />
                ))}
              </InfiniteScroll>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default FilterProductList;

function ProductCheckRow(props: {
  product: IEcommerceProductHierarchyItem;
  isSelected: boolean;
  onCheckChange: (
    product: IEcommerceProductHierarchyItem,
    checked: boolean
  ) => void;
}) {
  const { product, isSelected, onCheckChange } = props;

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onCheckChange(product, event.target.checked);
  };

  return (
    <TableRow
      key={product.id}
      sx={{
        '&:last-child td, &:last-child th': {
          border: 0
        }
      }}
    >
      <TableCell padding="checkbox" sx={{ width: '3%' }}>
        <Stack direction="row" gap={1}>
          <Checkbox
            color="primary"
            checked={isSelected}
            onChange={handleCheckboxChange}
          />
        </Stack>
      </TableCell>
      <TableCell padding="none" sx={{ flex: 1 }} colSpan={2}>
        <ProductPostCard
          title={product.name}
          imageUrl={product.image}
          productLink={product.link}
        />
      </TableCell>
    </TableRow>
  );
}
